"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { useLocale } from "next-intl";
import type { Locale } from "@/i18n/routing";
import type { SectionsContent } from "@/content/sections";
import { t } from "@/lib/content";
import type { Localized } from "@/lib/content";
import { cn } from "@/lib/cn";
import { useInView } from "@/lib/use-in-view";
import { blurImageProps } from "@/lib/image-blur";

type Fact = {
  icon: string;
  iconAlt: string;
  /** Static display value when the entry is not numerically animated. */
  value?: string;
  /** Numeric target to count up to. When set, overrides `value`. */
  target?: number;
  /** Optional suffix appended to the animated number, e.g. "+". */
  suffix?: string;
  /** Inline unit, localized. */
  unit?: Localized;
  label: Localized;
};

const COUNT_DURATION_MS = 1600;

function useCountUps(targets: (number | undefined)[], start: boolean): number[] {
  const [values, setValues] = useState<number[]>(() => targets.map((t) => (t == null ? 0 : 0)));

  useEffect(() => {
    if (!start) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setValues(targets.map((t) => t ?? 0));
      return;
    }
    let raf = 0;
    const startTs = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTs;
      const p = Math.min(elapsed / COUNT_DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValues(targets.map((t) => (t == null ? 0 : Math.round(t * eased))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start]);

  return values;
}

export function FactsStrip({
  factsCopy,
}: {
  factsCopy: SectionsContent["factsCopy"];
}) {
  const locale = useLocale() as Locale;
  const facts: Fact[] = [
    {
      icon: "/icons/facts/time.png",
      iconAlt: "Stoppuhr",
      value: "60–120",
      unit: factsCopy.unitMinutes,
      label: factsCopy.pickupTime,
    },
    {
      icon: "/icons/facts/parcel.png",
      iconAlt: "ISG Paket",
      target: 100_000,
      suffix: "+",
      label: factsCopy.shipmentsPerYear,
    },
    {
      icon: "/icons/facts/europe.png",
      iconAlt: "Europa-Karte",
      target: 28,
      unit: factsCopy.unitCountries,
      label: factsCopy.serviceArea,
    },
    {
      icon: "/icons/facts/phone.png",
      iconAlt: "24/7 Headset",
      value: "24/7",
      label: factsCopy.humanDispatch,
    },
  ];
  const [ref, inView] = useInView<HTMLElement>();
  const counts = useCountUps(
    facts.map((f) => f.target),
    inView,
  );

  return (
    <section ref={ref} aria-label="Kennzahlen" className="border-y border-line-200 bg-canvas">
      <div className="container py-14 md:py-20">
        <ul className="grid grid-cols-2 gap-y-10 sm:grid-cols-4 sm:gap-y-0">
          {facts.map((fact, i) => {
            const display =
              fact.target != null
                ? `${counts[i].toLocaleString(locale)}${fact.suffix ?? ""}`
                : fact.value ?? "";
            const labelText = t(fact.label, locale) as string;
            return (
              <li
                key={labelText}
                className={cn("px-0 sm:px-6 lg:px-10", i > 0 && "sm:border-l sm:border-line-200")}
              >
                <Image
                  src={fact.icon}
                  {...blurImageProps(fact.icon)}
                  alt={fact.iconAlt}
                  width={624}
                  height={624}
                  sizes="96px"
                  className="h-24 w-24"
                />
                <p className="mt-4 font-display text-[1.75rem] md:text-3xl font-semibold leading-none tracking-[-0.012em] text-ink-900 tabnums">
                  {display}
                  {fact.unit ? (
                    <span className="ml-1.5 align-baseline text-base font-medium text-ink-500">
                      {t(fact.unit, locale) as string}
                    </span>
                  ) : null}
                </p>
                <p className="mt-3 max-w-[20ch] text-sm leading-snug text-ink-500">
                  {labelText}
                </p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
